import React, { useState, useRef, useEffect } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';

import axios from 'axios';

import { Formik, Form, Field, ErrorMessage } from 'formik';
import { classNames } from 'primereact/utils';
import { Toast } from 'primereact/toast';

const AVAILABLE_SEGMENTS = ['Cáncer', 'Epilepsia', 'Dolor Crónico', 'Salud Mental', 'Wellness', 'Sin Diagnóstico', 'Otro'];

const Diagnostic = ({ quotation }) => {
    const [usages, setUsages] = useState([]);
    const [saleChannels, setSaleChannels] = useState([]);
    const [withdrawalTypes, setWithdrawalTypes] = useState([]);
    const [paymentTypes, setPaymentTypes] = useState([]);
    const [saving, setSaving] = useState(false);

    const toast = useRef(null);

    const initialValues = {
        diagnostic: '',
        selectedUsage: undefined,
        selectedSegments: [],
        selectedSaleChannel: undefined,
        selectedPaymentType: undefined,
        selectedWithdrawalType: undefined,
        deliveryDate: undefined,
        observations: '',
    };

    const validateForm = (data) => {
        let errors = {};

        if (!data.diagnostic) {
            errors.diagnostic = 'Por favor ingresa el diagnóstico del paciente';
        }

        if (!data.selectedSegments || data.selectedSegments.length === 0) {
            errors.selectedSegments = 'Por favor selecciona los segmentos';
        }

        if (!data.selectedUsage) {
            errors.selectedUsage = 'Por favor selecciona el tipo de uso';
        }

        if (!data.selectedSaleChannel) {
            errors.selectedSaleChannel = 'Por favor selecciona el canal de venta';
        }

        if (!data.selectedPaymentType) {
            errors.selectedPaymentType = 'Por favor selecciona la forma de pago';
        }

        if (!data.selectedWithdrawalType) {
            errors.selectedWithdrawalType = 'Por favor selecciona el método de despacho';
        }

        if (!data.deliveryDate) {
            errors.deliveryDate = 'Por favor ingresa la fecha de entrega';
        }

        return errors;
    };

    const onSubmit = () => {
        setSaving(true);
        axios
            .put('/quotations/' + quotation.id_registro + '/diagnostic', {
                quotation: formikRef.current.values,
            })
            .then(() => {
                toast.current.clear();
                toast.current.show({ severity: 'success', detail: 'Enviado correctamente' });
            })
            .catch(() => {
                toast.current.clear();
                toast.current.show({ severity: 'error', detail: 'Ha ocurrido un error al intentar grabar' });
            })
            .finally(() => {
                setSaving(false);
            });
    };

    useEffect(() => {
        axios.get('/api/usages').then((response) => {
            setUsages(response.data);
        });

        axios.get('/api/sale-channels').then((response) => {
            setSaleChannels(response.data);
        });

        axios.get('/api/withdrawal-types').then((response) => {
            setWithdrawalTypes(response.data);
        });

        axios.get('/api/payment-types').then((response) => {
            setPaymentTypes(response.data);
        });

        let deliveryDate = undefined;
        if (quotation.fecha_entrega) {
            const dateParts = quotation.fecha_entrega.split('-');
            deliveryDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1] - 1), parseInt(dateParts[2]));
        }

        formikRef.current.setValues({
            diagnostic: quotation.diagnostico || '',
            selectedSegments: quotation.segmentos ? quotation.segmentos.split(',') : [],
            selectedUsage: quotation.uso,
            selectedSaleChannel: quotation.canal_venta,
            selectedPaymentType: quotation.forma_pago,
            selectedWithdrawalType: quotation.tipo_despacho,
            deliveryDate,
            observations: quotation.otras_observaciones || '',
        });
    }, []);

    const formikRef = useRef(null);

    return (
        <>
            <Toast ref={toast} position="bottom-right" />
            <div>
                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validate={validateForm}
                    onSubmit={onSubmit}
                    validateOnChange={true}
                    validateOnBlur={false}
                    validateOnMount={false}
                >
                    <Form>
                        <div className="row justify-content-center">
                            <div className="col-12 col-lg-8 pb-4">
                                <div>
                                    <label htmlFor="diagnostic">Diagnóstico:</label>
                                    <Field name="diagnostic">
                                        {({ field, meta }) => (
                                            <>
                                                <InputText
                                                    {...field}
                                                    placeholder="Diagnóstico"
                                                    maxLength={300}
                                                    className={classNames('form-component', { 'p-invalid': meta.touched && meta.error })}
                                                />
                                            </>
                                        )}
                                    </Field>
                                    <ErrorMessage name="diagnostic" component="div" className="p-error" />
                                </div>
                            </div>

                            <div className="col-12 col-lg-4 pb-4">
                                <div>
                                    <label htmlFor="selectedSegments">Segmentos:</label>
                                    <Field name="selectedSegments">
                                        {({ field, meta }) => (
                                            <>
                                                <MultiSelect
                                                    {...field}
                                                    className={classNames('form-component', { 'p-invalid': meta.touched && meta.error })}
                                                    options={AVAILABLE_SEGMENTS}
                                                    display="chip"
                                                    showSelectAll={false}
                                                    placeholder="Seleccione segmentos"
                                                />
                                            </>
                                        )}
                                    </Field>
                                    <ErrorMessage name="selectedSegments" component="div" className="p-error" />
                                </div>
                            </div>

                            <div className="col-12 col-lg-3 pb-4">
                                <div>
                                    <label htmlFor="selectedUsage">Uso:</label>
                                    <Field name="selectedUsage">
                                        {({ field, meta }) => (
                                            <>
                                                <Dropdown
                                                    {...field}
                                                    className={classNames('form-component', { 'p-invalid': meta.touched && meta.error })}
                                                    options={usages}
                                                    optionLabel="name"
                                                    optionValue="name"
                                                    placeholder="Seleccione tipo de uso"
                                                    disabled={!(quotation.rut_doctor === null || quotation.uso === null)}
                                                />
                                            </>
                                        )}
                                    </Field>
                                    <ErrorMessage name="selectedUsage" component="div" className="p-error" />
                                </div>
                            </div>

                            <div className="col-12 col-lg-3 pb-4">
                                <label htmlFor="selectedSaleChannel">Canal de venta:</label>
                                <Field name="selectedSaleChannel">
                                    {({ field, meta }) => (
                                        <>
                                            <Dropdown
                                                {...field}
                                                className={classNames('form-component', { 'p-invalid': meta.touched && meta.error })}
                                                options={saleChannels}
                                                optionLabel="name"
                                                optionValue="name"
                                                placeholder="Seleccione canal de venta"
                                            />
                                        </>
                                    )}
                                </Field>
                                <ErrorMessage name="selectedSaleChannel" component="div" className="p-error" />
                            </div>

                            <div className="col-12 col-lg-3 pb-4">
                                <label htmlFor="selectedPaymentType">Forma de pago:</label>
                                <Field name="selectedPaymentType">
                                    {({ field, meta }) => (
                                        <>
                                            <Dropdown
                                                {...field}
                                                className={classNames('form-component', { 'p-invalid': meta.touched && meta.error })}
                                                options={paymentTypes}
                                                optionLabel="name"
                                                optionValue="name"
                                                placeholder="Seleccione forma de pago"
                                            />
                                        </>
                                    )}
                                </Field>
                                <ErrorMessage name="selectedPaymentType" component="div" className="p-error" />
                            </div>

                            <div className="col-12 col-lg-3 pb-4">
                                <label htmlFor="selectedWithdrawalType">Método de despacho:</label>
                                <Field name="selectedWithdrawalType">
                                    {({ field, meta }) => (
                                        <>
                                            <Dropdown
                                                {...field}
                                                className={classNames('form-component', { 'p-invalid': meta.touched && meta.error })}
                                                options={withdrawalTypes}
                                                optionLabel="name"
                                                optionValue="name"
                                                placeholder="Seleccione método de despacho"
                                            />
                                        </>
                                    )}
                                </Field>
                                <ErrorMessage name="selectedWithdrawalType" component="div" className="p-error" />
                            </div>

                            <div className="col-12 col-lg-3 pb-4">
                                <label htmlFor="deliveryDate">Fecha de entrega:</label>
                                <Field name="deliveryDate">
                                    {({ field, meta }) => (
                                        <>
                                            <Calendar
                                                {...field}
                                                className={classNames('form-component', { 'p-invalid': meta.touched && meta.error })}
                                                dateFormat="dd-mm-yy"
                                                placeholder="Fecha de entrega"
                                            />
                                        </>
                                    )}
                                </Field>
                                <ErrorMessage name="deliveryDate" component="div" className="p-error" />
                            </div>

                            <div className="col-12 col-lg-9 pb-4">
                                <label htmlFor="observations">Otras observaciones:</label>
                                <Field name="observations">
                                    {({ field, meta }) => (
                                        <>
                                            <InputText
                                                {...field}
                                                placeholder="Otras observaciones"
                                                maxLength={300}
                                                className={classNames('form-component', { 'p-invalid': meta.touched && meta.error })}
                                            />
                                        </>
                                    )}
                                </Field>
                                <ErrorMessage name="observations" component="div" className="p-error" />
                            </div>
                        </div>

                        <div className="text-center">
                            <Button type="submit" label="Grabar datos diagnóstico" severity="secondary" loading={saving} />
                        </div>
                    </Form>
                </Formik>
            </div>
        </>
    );
};

export default Diagnostic;
